import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseurl } from "../../Baseurl";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";

const PaymentMethod = () => {
  const navigate = useNavigate();

  // Payment Method states
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Load payment methods on component mount
  useEffect(() => {
    loadPaymentMethods();
  }, []);

  // Load all payment methods
  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`getAllPaymentMethods`);
      if (response.data?.success) {
        setPaymentMethods(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
      Swal.fire("Error", "Failed to load payment methods", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle payment method form submission
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!paymentFormData.name.trim()) {
      Swal.fire("Error", "Please enter payment method name", "error");
      return;
    }

    try {
      setLoading(true);
      if (editingPayment) {
        // Update existing payment method
        const response = await axiosInstance.put(
          `updatePaymentMethod/${editingPayment.id}`,
          {
            name: paymentFormData.name,
          }
        );

        if (response.data?.success) {
          Swal.fire(
            "Success",
            "Payment method updated successfully",
            "success"
          );
          loadPaymentMethods();
          setPaymentModalOpen(false);
          setEditingPayment(null);
          setPaymentFormData({ name: "" });
        }
      } else {
        // Create new payment method
        const response = await axiosInstance.post(`createPaymentMethod`, {
          name: paymentFormData.name,
        });

        if (response.data?.success) {
          Swal.fire(
            "Success",
            "Payment method created successfully",
            "success"
          );
          loadPaymentMethods();
          setPaymentModalOpen(false);
          setPaymentFormData({ name: "" });
        }
      }
    } catch (error) {
      console.error("Error saving payment method:", error);
      Swal.fire("Error", "Failed to save payment method", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit payment method
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentFormData({ name: payment.name });
    setPaymentModalOpen(true);
  };

  // Handle delete payment method
  const handleDeletePayment = async (paymentId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await axiosInstance.delete(
          `deletePaymentMethod/${paymentId}`
        );

        if (response.data?.success) {
          Swal.fire("Deleted!", "Payment method has been deleted.", "success");
          loadPaymentMethods();
        }
      } catch (error) {
        console.error("Error deleting payment method:", error);
        Swal.fire("Error", "Failed to delete payment method", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddPayment = () => {
    setEditingPayment(null);
    setPaymentFormData({ name: "" });
    setPaymentModalOpen(true);
  };

  const handleModalClose = () => {
    setPaymentModalOpen(false);
    setEditingPayment(null);
    setPaymentFormData({ name: "" });
  };

  // Filter payment methods based on search query
  const filteredPaymentMethods = paymentMethods.filter((payment) =>
    payment.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="row">
          <div className="col-md-12">
            <div className="d-flex">
              <h5 className="mainTitle backToReal">
                <i class="fa fa-arrow-left" onClick={() => navigate(-1)}></i>
                Payment Methods
              </h5>
            </div>
            <div className="table-card">
              <div className="tableHeader d-flex justify-content-between align-items-center">
                <div className="tableSearch">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search payment methods..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <span className="searchIcon">
                    <svg
                      xmlns="http:www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="gray"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "10px",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </span>
                </div>

                <button
                  type="button"
                  className="bgBtn"
                  onClick={handleAddPayment}
                >
                  Add Payment Method
                </button>
              </div>
              <div>
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SN</th>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Created At</th>
                          <th>Updated At</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPaymentMethods.length > 0 ? (
                          filteredPaymentMethods.map((payment, index) => (
                            <tr key={payment.id || index}>
                              <td>{index + 1}</td>
                              <td>{payment.id}</td>
                              <td>{payment.name}</td>
                              <td>
                                {payment.created_at
                                  ? new Date(
                                    payment.created_at
                                  ).toLocaleDateString("en-GB")
                                  : "N/A"}
                              </td>
                              <td>
                                {payment.updated_at
                                  ? new Date(
                                    payment.updated_at
                                  ).toLocaleDateString("en-GB")
                                  : "N/A"}
                              </td>
                              <td className="text-center">
                                <div className="d-flex justify-content-center">
                                  <div
                                    onClick={() => handleEditPayment(payment)}
                                    style={{ cursor: "pointer" }}
                                    className="avtar avtar-xs btn-link-secondary editIcon"
                                    title="Edit"
                                  >
                                    <i className="ti ti-edit f-20" />
                                    <span>Edit</span>
                                  </div>
                                  <div
                                    style={{ cursor: "pointer" }}
                                    className="avtar avtar-xs btn-link-secondary deleteIcon"
                                    onClick={() =>
                                      handleDeletePayment(payment.id)
                                    }
                                    title="Delete"
                                  >
                                    <i className="ti ti-trash f-20" />
                                    <span>Delete</span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">
                              {searchQuery
                                ? "No payment methods found matching your search"
                                : "No payment methods found"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      {paymentModalOpen && (
        <div
          className="modal show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingPayment
                    ? "Edit Payment Method"
                    : "Add Payment Method"}
                </h5>

              </div>
              <form onSubmit={handlePaymentSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Payment Method Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={paymentFormData.name}
                      onChange={(e) =>
                        setPaymentFormData({ name: e.target.value })
                      }
                      placeholder="Enter payment method name"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancelText"
                    onClick={handleModalClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {editingPayment ? "Updating..." : "Adding..."}
                      </>
                    ) : editingPayment ? (
                      "Update"
                    ) : (
                      "Add"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethod;
